export type ActionTier = 'low' | 'medium' | 'high';

/**
 * Which Forecast entity is being mutated. Echoed back to the gateway on
 * resolve so it can dispatch to the right MCP confirm tool.
 * wire contract (see ai-agents:
 * docs/superpowers/specs/2026-06-23-tnsr-247-hitl-rework-design.md).
 * 2026-07-10: added 'project' with subtype 'edit' for date updates.
 */
export type EntityType =
	| 'task'
	| 'phase'
	| 'allocation'
	| 'time_registration'
	| 'placeholder'
	| 'project'
	| 'team_member'
	| 'project_owner'
	| 'deliverable'
	| 'baseline_role';

/**
 * The plan / confirm subtype within an entity_type. Four subtypes exist
 * for `task` (reassignment / status_update / date_offset / date_set), one for
 * `phase` and `allocation` (date_offset), two for `time_registration`
 * (edit / create). `from_baseline` is a `placeholder`-only subtype (bulk
 * create, one entity per staged baseline role) distinct from `placeholder`'s
 * single-entity `create`. The gateway dispatch is keyed on
 * (entity_type, entity_subtype). `create` deliberately has no partial-
 * selection support — the create plan has no per-entity exclude semantic.
 */
export type EntitySubtype =
	| 'reassignment'
	| 'status_update'
	| 'date_offset'
	| 'date_set'
	| 'edit'
	| 'create'
	| 'stage_update'
	| 'assignment'
	| 'from_baseline'
	| 'duplicate';

export type FieldType =
	| 'text'
	| 'number'
	| 'duration'
	| 'select'
	| 'multiselect'
	| 'date'
	| 'daterange'
	| 'boolean'
	| 'readonly';

export type FieldOption = {id: string; label: string};

export type FieldConstraints = {
	min?: number;
	max?: number;
	minDate?: string;
	maxDate?: string;
	pattern?: string;
	maxLength?: number;
};

/** typed write-form descriptor. See spec §2.1. */
export type FieldDescriptor = {
	key: string;
	label: string;
	type: FieldType;
	value: unknown;
	editable: boolean;
	required?: boolean;
	options?: FieldOption[];
	optionsSource?: string;
	constraints?: FieldConstraints;
	display?: string;
};

const VALID_FIELD_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
	'text',
	'number',
	'duration',
	'select',
	'multiselect',
	'date',
	'daterange',
	'boolean',
	'readonly',
]);

export type AffectedItem = {
	id: string;
	/**
	 * Pre-dedup server id. `id` is suffixed on collisions (`${rawId}-2`) to keep
	 * React keys unique; `originalId` preserves the real entity id used to build
	 * `exclude_ids` (the gateway's `list[int]` contract), so a suffixed row still
	 * resolves to its numeric id.
	 */
	originalId?: string;
	title: string;
	description: string;
	/** Read-only key→value rows (PROJECT / ASSIGNEE / DATES …). supplies the editable version. */
	kv?: Record<string, string>;
	/** typed write-form field descriptors. Present when the AI emits structured fields[]. */
	fields?: FieldDescriptor[];
	/** App-absolute path rooted at "/" (e.g. /project/P1/tasks/10234). */
	view_url?: string;
};

export type ActionPlan = {
	action_id: string;
	title: string;
	summary: string;
	tier: ActionTier;
	/** added so resolve can dispatch without a round-trip. */
	entity_type: EntityType;
	/**  added so resolve can dispatch without a round-trip. */
	entity_subtype: EntitySubtype;
	affected_count: number;
	affected: AffectedItem[];
	warnings: string[];
	expires_at?: string;
};

export type ActionResult = {
	/**
	 * `partial`: server-side claim succeeded but some entities failed — distinct from `failed`
	 *   (no claim, transient error, retryable). Renders a non-Retry distinct state.
	 * `expired`: the plan's TTL passed before the user confirmed (or a concurrent confirm
	 *   already consumed it). No Retry affordance — the plan can't come back.
	 */
	status: 'done' | 'failed' | 'partial' | 'expired';
	succeededIds?: string[];
	failed?: Array<{id: string; reason: string}>;
	message?: string;
	/**
	 * Optional guard code so the FE can switch UX affordance without parsing message strings.
	 * `expired` and `drifted`/`wrong_tool` are non-retryable; `transport` and null are retryable.
	 */
	guard?: 'expired' | 'wrong_tool' | 'drifted' | 'transport' | null;
	/**
	 * Relay global id of the created placeholder's project. Present only on a
	 * successful placeholder-create confirm (surfaced by the gateway from the
	 * MCP confirm result); used to open the allocation modal.
	 */
	projectId?: string;
};

export const ActionPhase = {
	Thinking: 'thinking',
	Pending: 'pending',
	Selected: 'selected',
	Confirming: 'confirming',
	Done: 'done',
	Failed: 'failed',
	Partial: 'partial',
	Expired: 'expired',
	Paused: 'paused',
} as const;

export type ActionPhase = (typeof ActionPhase)[keyof typeof ActionPhase];

export type HistoryProp = {push: (path: string) => void} | null | undefined;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

const VALID_ENTITY_TYPES: ReadonlySet<EntityType> = new Set<EntityType>([
	'task',
	'phase',
	'allocation',
	'time_registration',
	'placeholder',
	'project',
	'team_member',
	'project_owner',
	'deliverable',
	'baseline_role',
]);
const VALID_ENTITY_SUBTYPES: ReadonlySet<EntitySubtype> = new Set<EntitySubtype>([
	'reassignment',
	'assignment',
	'status_update',
	'date_offset',
	'date_set',
	'edit',
	'create',
	'stage_update',
	'from_baseline',
	'duplicate',
]);

function parseFields(raw: unknown): FieldDescriptor[] | undefined {
	if (!Array.isArray(raw)) return undefined;
	const seenKeys = new Set<string>();
	const fields = raw
		.filter(isObject)
		.filter(f => typeof f.key === 'string' && (f.key as string).length > 0)
		.filter(f => typeof f.type === 'string' && VALID_FIELD_TYPES.has(f.type as FieldType))
		// Drop a later field that repeats an earlier field.key — a duplicate key
		// collides as a React key and merges two rows' edit state downstream.
		.filter(f => {
			const k = f.key as string;
			if (seenKeys.has(k)) return false;
			seenKeys.add(k);
			return true;
		})
		.map(f => ({
			key: f.key as string,
			label: String(f.label ?? ''),
			type: f.type as FieldType,
			value: f.value,
			editable: f.editable === true,
			required: f.required === true ? true : undefined,
			options: Array.isArray(f.options)
				? (f.options as unknown[])
						.filter(isObject)
						.filter(o => o.id !== undefined && o.id !== null)
						.map(o => ({id: String(o.id), label: String(o.label ?? '')}))
				: undefined,
			optionsSource: typeof f.optionsSource === 'string' ? f.optionsSource : undefined,
			constraints: isObject(f.constraints) ? (f.constraints as FieldConstraints) : undefined,
			display: typeof f.display === 'string' ? f.display : undefined,
		}));
	return fields.length > 0 ? fields : undefined;
}

/** Parse `user_approval_tool` arguments into an ActionPlan. Returns null if the shape is invalid. */
export function parseActionPlan(raw: unknown): ActionPlan | null {
	if (!isObject(raw)) return null;
	const {action_id, title, summary, tier, affected, entity_type, entity_subtype} = raw;
	if (typeof action_id !== 'string' || typeof title !== 'string' || typeof summary !== 'string') return null;
	if (tier !== 'low' && tier !== 'medium' && tier !== 'high') return null;
	if (!Array.isArray(affected)) return null;

	// entity_type + entity_subtype must be present on
	// every emitted card. They are not optional in the wire contract — the
	// FE echoes both back to the resolve endpoint and the gateway dispatches
	// on them.
	//
	// entity_type: if missing/invalid we default to the safest no-op route
	// ("task") and log a warning so a wire-contract drift shows up loudly in
	// dev, since a bogus entity_type alone doesn't change which confirm tool
	// gets dispatched.
	//
	// entity_subtype: this DOES select the confirm tool (gateway dispatch is
	// keyed on (entity_type, entity_subtype)). Defaulting it would silently
	// mislabel the card as a different plan type — e.g. an unrecognized
	// subtype for a new plan tool would render and confirm as if it were a
	// 'reassignment' card, and the gateway would then dispatch
	// confirm_task_reassignment against a plan that isn't one, which fails
	// with a confusing "not a reassignment plan" error instead of just not
	// showing the card. So an invalid/missing entity_subtype drops the whole
	// card (returns null) rather than defaulting.
	let parsedEntityType: EntityType = 'task';
	if (typeof entity_type === 'string' && VALID_ENTITY_TYPES.has(entity_type as EntityType)) {
		parsedEntityType = entity_type as EntityType;
	} else if (entity_type !== undefined) {
		console.warn('[ActionPlan] invalid entity_type', entity_type, '— defaulting to "task"');
	} else {
		console.warn('[ActionPlan] missing entity_type — defaulting to "task"');
	}
	if (typeof entity_subtype !== 'string' || !VALID_ENTITY_SUBTYPES.has(entity_subtype as EntitySubtype)) {
		console.warn('[ActionPlan] invalid or missing entity_subtype', entity_subtype, '— dropping card');
		return null;
	}
	const parsedEntitySubtype: EntitySubtype = entity_subtype as EntitySubtype;

	const seenIds = new Map<string, number>();
	const usedIds = new Set<string>();
	const items: AffectedItem[] = affected
		.filter(isObject)
		.filter(it => typeof it.id === 'string' && (it.id as string).length > 0)
		.map(it => {
			const rawId = it.id as string;
			let count = seenIds.get(rawId) ?? 0;
			// A suffixed id (`${rawId}-2`) can still equal another entity's rawId,
			// which would duplicate the React key and merge two rows' selection /
			// edit state. Keep advancing the suffix until the id is unused.
			let id = count === 0 ? rawId : `${rawId}-${count + 1}`;
			while (usedIds.has(id)) {
				count += 1;
				id = `${rawId}-${count + 1}`;
			}
			seenIds.set(rawId, count + 1);
			usedIds.add(id);
			return {
				id,
				originalId: rawId,
				title: String(it.title ?? ''),
				description: String(it.description ?? ''),
				kv: isObject(it.kv) ? (it.kv as Record<string, string>) : undefined,
				fields: parseFields(it.fields),
				view_url: typeof it.view_url === 'string' ? it.view_url : undefined,
			};
		});

	const warnings = Array.isArray(raw.warnings) ? raw.warnings.map(String) : [];
	const affected_count = typeof raw.affected_count === 'number' ? raw.affected_count : items.length;

	return {
		action_id,
		title,
		summary,
		tier,
		entity_type: parsedEntityType,
		entity_subtype: parsedEntitySubtype,
		affected_count,
		affected: items,
		warnings,
		expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : undefined,
	};
}
